import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { LogIn, AlertCircle, Loader, ArrowLeft } from 'lucide-react';
import LogoBlanco from '../components/img/LogoNombre.png';
import { api } from '../services/api';

type LoginStep = 'credentials' | 'verification';

interface LoginResponse {
  status: 'verificacion_pendiente';
  idUsuario: number;
  mensaje: string;
}

interface VerifyResponse {
  status: 'verificado';
  expiresIn: string;
}

export function Login() {
  const [step, setStep] = useState<LoginStep>('credentials');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [codigo, setCodigo] = useState('');
  const [idUsuario, setIdUsuario] = useState<number | null>(null);
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { isAuthenticated, checkAuth } = useAuth();
  const navigate = useNavigate();

  // PASO 1: Validar credenciales y enviar código
  const handleSubmitCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await api.post<LoginResponse>('/auth/login', {
        usuario: username.trim(),
        pass: password,
      }).then(res => res.data);

      if (response.status === 'verificacion_pendiente') {
        setIdUsuario(response.idUsuario);
        setStep('verification');
        setPassword(''); // Limpiar contraseña por seguridad
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || 
        'Error al iniciar sesión. Verifique sus credenciales.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // PASO 2: Validar código OTP
  const handleSubmitVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (!idUsuario) {
        setError('Sesión inválida. Intente nuevamente.');
        return;
      }

      if (codigo.length !== 6 || isNaN(Number(codigo))) {
        setError('Código debe ser un número de 6 dígitos');
        return;
      }

      const response = await api.post<VerifyResponse>('/auth/verify-code', {
        idUsuario,
        codigo: codigo.trim(),
      }).then(res => res.data);

      if (response.status === 'verificado') {
        await checkAuth(); // Esto recarga el estado del AuthContext
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || 
        'Código incorrecto o expirado. Intente nuevamente.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Volver al paso de credenciales
  const handleBackToCredentials = () => {
    setStep('credentials');
    setCodigo('');
    setIdUsuario(null);
    setError(null);
  };

  // Redirigir si ya está autenticado
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary p-3 md:p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-3 md:space-y-4 bg-white">
          <div className="mx-auto w-50 md:w-50 mb-2 md:mb-4">
            <img 
              src={LogoBlanco} 
              alt="Logo AGEMI" 
              className="w-full h-auto object-contain"
            />
          </div>
          <CardTitle className="text-xl md:text-3xl text-primary font-bold">AGEMI</CardTitle>
          <p className="text-xs md:text-sm text-primary">
            {step === 'credentials' 
              ? 'Ingrese sus credenciales'
              : 'Ingrese el código enviado por SMS'
            }
          </p>
        </CardHeader>
        <CardContent>
          {step === 'credentials' ? (
            // PASO 1: Credenciales
            <form onSubmit={handleSubmitCredentials} className="space-y-4">
              <Input
                label="Usuario"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Ingrese su usuario"
                disabled={isLoading}
                required
              />
              <Input
                label="Contraseña"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ingrese su contraseña"
                disabled={isLoading}
                required
              />
              {error && (
                <div className="p-3 bg-destructive/10 border border-destructive rounded-lg text-destructive text-xs md:text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <Button 
                type="submit" 
                variant="primary" 
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4 mr-2" />
                    Continuar
                  </>
                )}
              </Button>
            </form>
          ) : (
            // PASO 2: Código OTP
            <form onSubmit={handleSubmitVerification} className="space-y-4">
              <div className="bg-primary/10 border border-primary/0 rounded-lg p-3 text-xs md:text-sm text-primary text-center">
                <p className="font-semibold mb-1">Código enviado por SMS y correo</p>
                <p>Hemos enviado un código de 6 dígitos al teléfono y correo registrados</p>
              </div>
              
              <Input
                label="Código de Verificación"
                type="text"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                disabled={isLoading}
                required
                className="text-center text-2xl tracking-widest"
              />

              {error && (
                <div className="p-3 bg-destructive/10 border border-destructive rounded-lg text-destructive text-xs md:text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button 
                type="submit" 
                variant="primary" 
                className="w-full"
                disabled={isLoading || codigo.length !== 6}
              >
                {isLoading ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  'Verificar Código'
                )}
              </Button>

              <Button 
                type="button" 
                className="w-full"
                onClick={handleBackToCredentials}
                disabled={isLoading}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>

              <p className="text-xs md:text-center text-gray-500">
                ¿No recibió el código? Vuelva e intente nuevamente
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}